import React from 'react';
import { Mui, MuiIcons } from '@osu/react-ui';
import ScholarsPreferencingForm from '../scholars/ScholarsPreferencingForm';
import { useGetOpenScholarsProgramsQuery } from '../../../services/graphql/api';
import useUserEmplid from '../../hooks/useUserEmplid';

/**
 * PreferencesFormWrapper for StudentDashboard (has489).
 * Wraps the ranked Scholars preferencing form (Form A) with styling and title.
 *
 * Renders the real ranked-preference form (ranks 1-3 required, 4-8 optional) fed by the
 * live list of OPEN Scholars programs for the current cycle term. The form submits directly
 * through the owner-scoped submitScholarsPreferences mutation, which writes the preferences
 * and progresses the application to SCHOLARS_PREFERENCES_SUBMITTED. Term is resolved
 * server-side, so it is not supplied from the client.
 */
function PreferencesFormWrapper({ step, applicationData }) {
  // The applicant's emplid comes from the authenticated session (osuid), NOT the getSelfService
  // payload (which has no emplid field). In admin view-only this returns the viewed student's emplid.
  const { emplid: sessionEmplid } = useUserEmplid();

  // Only fetch open programs once the form is actually shown (avoids a fetch per timeline step).
  const { data: programs = [], isLoading, isError } = useGetOpenScholarsProgramsQuery(undefined, {
    skip: !step?.showPreferencesForm,
  });

  if (!step.showPreferencesForm) return null;

  const applicantEmployeeId =
    sessionEmplid || applicationData?.emplid || applicationData?.honors?.emplid || applicationData?.scholars?.emplid;

  return (
    <Mui.Box
      sx={{
        mt: 3,
        width: '100%',
        boxSizing: 'border-box',
        p: { xs: 1, sm: 3 },
        backgroundColor: 'background.paper',
        borderRadius: 2,
        border: '2px solid',
        borderColor: '#BA0C2F',
        boxShadow: 3,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -2,
          left: -2,
          right: -2,
          bottom: -2,
          background: 'linear-gradient(45deg, #BA0C2F, #A7B1B7)',
          borderRadius: 2,
          zIndex: -1
        }
      }}
      role="region"
      aria-labelledby="preferences-form-title"
      tabIndex={-1}
    >
      <Mui.Typography
        id="preferences-form-title"
        variant="h6"
        gutterBottom
        sx={{
          mb: { xs: 2, sm: 3 },
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: { xs: 0, sm: 2 },
          fontSize: { xs: '1.05rem', sm: '1.25rem' },
          color: '#BA0C2F'
        }}
      >
        <MuiIcons.Assignment sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
        Complete Your Program Preferences
      </Mui.Typography>

      {isLoading && (
        <Mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
          <Mui.CircularProgress size={20} />
          <Mui.Typography variant="body2">Loading available programs…</Mui.Typography>
        </Mui.Box>
      )}

      {isError && (
        <Mui.Alert severity="error" sx={{ mb: 2 }}>
          Unable to load available Scholars programs. Please refresh and try again.
        </Mui.Alert>
      )}

      {!isLoading && !isError && programs.length === 0 && (
        <Mui.Alert severity="info" sx={{ mb: 2 }}>
          There are no Scholars programs open for preferencing at this time.
        </Mui.Alert>
      )}

      {!isLoading && !isError && programs.length > 0 && (
        <ScholarsPreferencingForm
          applicantEmployeeId={applicantEmployeeId}
          programs={programs}
        />
      )}
    </Mui.Box>
  );
}

export default PreferencesFormWrapper;
